import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import Typography from "components/basic/typography";
import { useGetEvChargerServiceListByVendorQuery } from "generated/graphql";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMeQuery, VendorVerification } from "generated/graphql";
import { useSnack } from "hooks/useSnack";

const ServiceOffering: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("services");

  const [{ data: meData }] = useMeQuery();
  const vendorId = meData?.me?.vendor?.id || 0;
  const installs = meData?.me?.vendor?.offeringEvInstallation === "Yes";
  const approved =
    meData?.me?.vendor?.verificationStatus === VendorVerification.Approved;
  const [{ data: fetchedData }, refetchServices] =
    useGetEvChargerServiceListByVendorQuery({
      variables: { vendorId: vendorId },
      requestPolicy: "network-only",
    });

  // Auto-refetch when page becomes visible again (user returns from another tab/app)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchServices({ requestPolicy: "network-only" });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [refetchServices]);

  // TODO: Set up automatic polling every 10 seconds or need to install websocket to create bi-directional connection
  // useEffect(() => {
  //   const pollInterval = setInterval(() => {
  //     refetchServices({ requestPolicy: "network-only" });
  //   }, 10000);

  //   return () => clearInterval(pollInterval);
  // }, [refetchServices]);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: theme.spacing(2),
          padding: theme.spacing(1.8),
          height: "100%",
        }}
      >
        <Card
          label={t("list.add")}
          onClick={() => {
            if (!approved) {
              showSnack(t("list.not_approved"), "info");
            } else if (installs) {
              navigate("/dashboard/ServiceInstallation");
            } else {
              showSnack(t("list.enable_installation"), "warning");
            }
          }}
        />

        {/* Dynamic cards for each fetched service */}
        {fetchedData?.getEVChargerServiceListByVendor?.map((service: any) => (
          <Card
            key={service.id}
            label={t("list.service_id", { id: service.id })}
            onClick={() => {
              navigate(
                `/dashboard/ServiceInstallation?currentPage=reviewAndUpdate&serviceId=${service.id}`,
              );
            }}
            isAddCard={false}
            serviceData={service}
          />
        ))}
      </div>
    </div>
  );
};

const Card: React.FC<{
  label: string;
  onClick: () => void;
  isAddCard?: boolean;
  serviceData?: any;
}> = ({ label, onClick, isAddCard = true, serviceData }) => {
  const theme = useTheme();
  const { t } = useTranslation("services");
  const format = useFormat();

  const [isHovered, setIsHovered] = useState(false);

  const cardStyle: React.CSSProperties = {
    display: "flex",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    width: "450px",
    height: "300px",
    transition: "transform 0.2s ease, background-color 0.3s",
    padding: theme.spacing(2),
    textAlign: "center",
  };

  const plusStyle: React.CSSProperties = {
    fontWeight: "bold",
    fontSize: theme.typography.h1.fontSize,
    marginBottom: theme.spacing(1.4),
  };

  const hoverCardStyle: React.CSSProperties = {
    ...cardStyle,
    backgroundColor: theme.palette.common.black,
    transform: "scale(1.03)",
  };

  return (
    <div
      style={isHovered ? hoverCardStyle : cardStyle}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isAddCard && <div style={plusStyle}>+</div>}

      <Typography
        variant="h3"
        style={{ fontWeight: "bold", marginBottom: theme.spacing(1) }}
      >
        {label}
      </Typography>

      {!isAddCard && serviceData && (
        <div style={{ fontSize: "14px", opacity: 0.9 }}>
          <div>
            {t("list.created", {
              date: format.date(parseInt(serviceData.createdAt)),
            })}
          </div>
          {serviceData.id && <div>{t("list.id", { id: serviceData.id })}</div>}
        </div>
      )}
    </div>
  );
};

export default ServiceOffering;
